'use client';

import Link from 'next/link';
import { useState } from 'react';

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !message.trim()) {
      setErrorMessage('Please fill in all required fields.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('/api/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          _honeypot: '',
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('Unable to connect. Please check your internet connection and try again.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-light-text-secondary dark:text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          <span className="text-light-text dark:text-foreground">Contact</span>
        </nav>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            Have a question, suggestion, or want to contribute? We&rsquo;d love to hear from you.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="md:col-span-2">
            {status === 'success' ? (
              <div
                role="status"
                aria-live="polite"
                className="bg-accent-muted border border-border rounded-lg p-8"
              >
                <p className="text-accent font-bold text-xl mb-3">
                  Message sent
                </p>
                <p className="text-light-text-secondary dark:text-foreground-secondary">
                  Thanks for reaching out. We&rsquo;ll get back to you as soon as we can.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-accent hover:text-accent font-medium transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
                  >
                    Name <span className="text-accent">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    maxLength={100}
                    className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
                    disabled={status === 'loading'}
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
                  >
                    Email <span className="text-accent">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
                    disabled={status === 'loading'}
                  />
                </div>

                {/* Subject */}
                <div>
                  <label
                    htmlFor="contact-subject"
                    className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
                  >
                    Subject
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="What is this about?"
                    maxLength={200}
                    className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
                    disabled={status === 'loading'}
                  />
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
                  >
                    Message <span className="text-accent">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Your message..."
                    required
                    rows={6}
                    maxLength={5000}
                    className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all resize-y"
                    disabled={status === 'loading'}
                  />
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    {message.length}/5000
                  </p>
                </div>

                {/* Honeypot (hidden) */}
                <div className="hidden" aria-hidden="true">
                  <input type="text" name="_honeypot" tabIndex={-1} autoComplete="off" />
                </div>

                {/* Error Message */}
                {status === 'error' && errorMessage && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"
                  >
                    <p className="text-red-600 dark:text-red-400 text-sm">{errorMessage}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-accent hover:bg-accent-hover disabled:bg-accent-muted0 disabled:cursor-not-allowed text-accent-foreground font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  {status === 'loading' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Direct Contact */}
            <div className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted">
              <h2 className="text-lg font-bold text-light-text dark:text-foreground mb-4">
                Other Ways to Reach Us
              </h2>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="font-medium text-light-text dark:text-foreground mb-1">Email</p>
                  <a
                    href="mailto:hello@theartofcto.com"
                    className="text-accent hover:text-accent transition-colors"
                  >
                    hello@theartofcto.com
                  </a>
                </div>
                <div>
                  <p className="font-medium text-light-text dark:text-foreground mb-1">Support</p>
                  <Link
                    href="/support"
                    className="text-accent hover:text-accent transition-colors"
                  >
                    Help &amp; Support Center
                  </Link>
                </div>
              </div>
            </div>

            {/* What to Expect */}
            <div className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted">
              <h2 className="text-lg font-bold text-light-text dark:text-foreground mb-4">
                What to Expect
              </h2>
              <ul className="space-y-3 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-0.5">&#10003;</span>
                  We read every message personally
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-0.5">&#10003;</span>
                  Typical response within 1-2 business days
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-0.5">&#10003;</span>
                  Topic suggestions are always welcome
                </li>
              </ul>
            </div>

            {/* Good For */}
            <div className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted">
              <h2 className="text-lg font-bold text-light-text dark:text-foreground mb-4">
                Get in Touch About
              </h2>
              <ul className="space-y-2 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>Content suggestions or feedback</li>
                <li>Guest contributions</li>
                <li>Partnership inquiries</li>
                <li>Bug reports or site issues</li>
                <li>General questions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
